import { type CSSProperties } from 'react';

const baseCellStyle: CSSProperties = {
  width: '100%',
  textAlign: 'center'
};

export const getTemperatureStyle = (value: number | null | undefined): CSSProperties => {
  if (value == null) return baseCellStyle;
  
  if (value <= 5) {
    // Dark blue at 5°F and below
    return {
      ...baseCellStyle,
      backgroundColor: 'rgb(0, 0, 139)',
      color: '#ffffff'
    };
  }
  
  if (value <= 25) {
    // Dark blue (0, 0, 139) at 5°F to white (255, 255, 255) at 25°F
    const intensity = (value - 5) / (25 - 5);
    const colorValue = Math.round(255 * intensity);
    const darkBlue = 139;
    const r = Math.round(colorValue);
    const g = Math.round(colorValue);
    const b = Math.round(darkBlue + (255 - darkBlue) * intensity);
    
    return {
      ...baseCellStyle,
      backgroundColor: `rgb(${r}, ${g}, ${b})`,
      color: intensity > 0.5 ? undefined : '#ffffff'
    };
  }
  
  if (value <= 32) {
    // White for temperatures between 25°F and 32°F
    return {
      ...baseCellStyle,
      backgroundColor: 'rgb(255, 255, 255)'
    };
  }
  
  // Red gradient for temperatures above 32°F
  const intensity = Math.min((value - 32) / 3, 1);
  const g = Math.round(255 * (1 - intensity));
  const b = Math.round(255 * (1 - intensity));
  
  return {
    ...baseCellStyle,
    backgroundColor: `rgb(255, ${g}, ${b})`,
    color: intensity > 0.5 ? '#ffffff' : undefined
  };
};

export const getSnowLevelStyle = (value: number | null | undefined, baseElevation: number, topElevation: number): CSSProperties => {
  if (value == null) return baseCellStyle;
  
  // Calculate intensity based on position between 500ft below base elevation and top elevation
  const adjustedBaseElevation = baseElevation - 500; // Start color scale 500ft below base
  const range = topElevation - adjustedBaseElevation;
  const intensity = Math.max(0, Math.min(1, (value - adjustedBaseElevation) / range));
  
  // White (255,255,255) to Purple (228,0,255) to Red (255,0,0)
  let r, g, b;
  
  if (intensity <= 0.5) {
    // White to Purple (first half)
    const i = intensity * 2; // Scale 0-0.5 to 0-1
    r = Math.round(255 - ((255 - 228) * i)); // 255 to 228
    g = Math.round(255 * (1 - i)); // 255 to 0
    b = 255;
  } else {
    // Purple to Red (second half)
    const i = (intensity - 0.5) * 2; // Scale 0.5-1 to 0-1
    r = Math.round(228 + (27 * i)); // 228 to 255
    g = 0;
    b = Math.round(255 * (1 - i)); // 255 to 0
  }
  
  // Calculate relative brightness for text color
  const brightness = (r + g + b) / (3 * 255); // 0 to 1 scale
  
  return {
    ...baseCellStyle,
    backgroundColor: `rgb(${r}, ${g}, ${b})`,
    color: brightness < 0.7 ? '#ffffff' : undefined
  };
};

export const getCloudCoverStyle = (value: number | null | undefined): CSSProperties => {
  if (value == null) return baseCellStyle;
  
  // Scale from light blue (0%) to dark grey (100%)
  const intensity = value / 100;
  const colorValue = Math.round(255 * (1 - intensity * 0.7)); // Reduce intensity by 30% to keep grey lighter
  
  // Start with light blue (rgb(176, 214, 255)) and transition to dark grey
  const r = Math.round(176 + (colorValue - 176) * intensity);
  const g = Math.round(214 + (colorValue - 214) * intensity);
  const b = Math.round(255 + (colorValue - 255) * intensity);
  
  return {
    ...baseCellStyle,
    backgroundColor: `rgb(${r}, ${g}, ${b})`,
    color: intensity > 0.6 ? '#ffffff' : undefined
  };
};

export const getWindSpeedStyle = (value: number | null | undefined): CSSProperties => {
  if (value == null) return baseCellStyle;
  if (value === 0) return { ...baseCellStyle, backgroundColor: '#ffffff' };

  // Scale from white (rgb(255,255,255)) to purple (rgb(128,0,128))
  const intensity = Math.min(value / 50, 1);
  const r = Math.round(255 - (127 * intensity)); // 255 to 128
  const g = Math.round(255 * (1 - intensity));   // 255 to 0
  const b = Math.round(255 - (127 * intensity)); // 255 to 128
  
  return {
    ...baseCellStyle,
    backgroundColor: `rgb(${r}, ${g}, ${b})`,
    color: intensity > 0.3 ? '#ffffff' : undefined
  };
};

export const getPrecipStyle = (value: number | null | undefined, groupInterval?: string): CSSProperties => {
  if (value == null) return baseCellStyle;
  if (value === 0) return { ...baseCellStyle, backgroundColor: '#ffffff' };

  // Define max values for different intervals
  const maxValues: { [key: string]: number } = {
    'none': 0.1,   // 1 hour (no grouping)
    '1': 0.1,      // 1 hour (explicit)
    '3': 0.2,      // 3 hour
    '6': 0.3,      // 6 hour
    '12': 0.6,     // 12 hour
    '24': 1.0      // 24 hour
  };

  const maxValue = maxValues[groupInterval || 'none'];
  if (value > maxValue) return { ...baseCellStyle, backgroundColor: '#3f49b0', color: '#ffffff' };
  
  const intensity = value / maxValue;
  const r = Math.round(255 - (255 - 63) * intensity);
  const g = Math.round(255 - (255 - 73) * intensity);
  const b = Math.round(255 - (255 - 176) * intensity);
  
  return {
    ...baseCellStyle,
    backgroundColor: `rgb(${r}, ${g}, ${b})`,
    color: intensity > 0.5 ? '#ffffff' : undefined
  };
};
